import { useState } from 'react';
import { useFormikContext } from 'formik';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';

import { LoadingButton } from '@@/buttons';

import { Chart } from '../types';
import { RegistryValue } from '../components/HelmRegistrySelect';
import { useIsRegistryAvailable } from '../upgrade/utils/useIsRegistryAvailable';
import { useHelmRegistryRepoOptions } from '../helmChartSourceQueries/useHelmRegistries';
import { ManifestPreviewFormSection } from '../components/ManifestPreviewFormSection';

import { HelmChartSelectionSection } from './HelmChartSelect';
import { InstallOptionsFormSection } from './InstallOptionsSection';
import { HelmDeployToSection } from './HelmDeployToSection';
import { HelmInstallFormValues } from './types';
import { HelmTemplatesSelectedItem } from './HelmTemplatesSelectedItem';

type Props = {
  namespaces: Array<{ label: string; value: string }>;
  setPreviewIsValid: (isValid: boolean) => void;
};

export function ManualInstallFormSections({
  namespaces,
  setPreviewIsValid,
}: Props) {
  const environmentId = useEnvironmentId();
  const { values, errors, setFieldValue, isSubmitting, isValid } =
    useFormikContext<HelmInstallFormValues>();

  const [selectedChart, setSelectedChart] = useState<Chart | null>(null);
  const [selectedRegistry, setSelectedRegistry] =
    useState<RegistryValue | null>(null);

  const registryOptionsQuery = useHelmRegistryRepoOptions(
    environmentId,
    values.namespace
  );
  const isRegistryAvailable = useIsRegistryAvailable(
    selectedRegistry,
    registryOptionsQuery.registryOptions
  );

  return (
    <>
      <HelmDeployToSection
        namespace={values.namespace}
        name={values.name}
        namespaces={namespaces}
        isNamespaceDisabled={!!selectedChart && !!selectedRegistry?.isOCI}
        errors={{
          namespace: errors.namespace,
          name: errors.name,
        }}
        onNamespaceChange={(value) => setFieldValue('namespace', value)}
        onNameChange={(value) => setFieldValue('name', value)}
      />
      {!selectedChart ? (
        // when a chart is not selected, show the chart selection section (initial state)
        <HelmChartSelectionSection
          namespace={values.namespace}
          onChartSelect={handleChartSelect}
        />
      ) : (
        // once a chart is selected, show the selected chart (clearable), install options and install button
        <>
          <HelmTemplatesSelectedItem
            selectedChart={selectedChart}
            clearHelmChart={handleClearChart}
          />
          <InstallOptionsFormSection
            selectedChart={selectedChart}
            selectedRegistry={selectedRegistry}
            version={values.version}
            values={values.values}
            errors={errors}
            onVersionChange={(version) => setFieldValue('version', version)}
            onValuesChange={(values) => setFieldValue('values', values)}
          />
          <div className="mb-8">
            <ManifestPreviewFormSection
              mode="manual"
              name={(values.name || '').trim()}
              namespace={values.namespace || ''}
              chart={selectedChart?.name || ''}
              version={values.version}
              repo={
                selectedRegistry?.registryId ? undefined : selectedChart?.repo
              }
              registryId={selectedRegistry?.registryId}
              values={values.values}
              onChangePreviewValidation={setPreviewIsValid}
              title="Manifest preview"
              environmentId={environmentId}
            />
          </div>
          <LoadingButton
            type="submit"
            className="!ml-0 mt-5"
            loadingText="Installing Helm chart..."
            isLoading={isSubmitting}
            disabled={!isRegistryAvailable || !isValid}
            data-cy="helm-install"
          >
            Install
          </LoadingButton>
        </>
      )}
    </>
  );

  function handleChartSelect(chart: Chart, registry: RegistryValue | null) {
    setSelectedChart(chart);
    setSelectedRegistry(registry);
    setFieldValue('chartName', chart.name);
    setFieldValue('chartRepo', chart.repo);
    setFieldValue('registryId', registry?.registryId);
    setFieldValue('version', chart.versions[0] ?? '');
  }

  function handleClearChart() {
    setSelectedChart(null);
    setSelectedRegistry(null);
    setFieldValue('chartName', undefined);
    setFieldValue('chartRepo', undefined);
    setFieldValue('version', '');
    setFieldValue('values', '');
  }
}
